import React, { useMemo, useState } from "react";
import {
  Card,
  Table,
  Space,
  Spin,
  Row,
  Col,
  Segmented,
  Typography,
  Tag,
  Tooltip,
  Divider,
} from "antd";
import {
  PartitionOutlined,
  LineChartOutlined,
  TableOutlined,
} from "@ant-design/icons";
import CohortCharts from "./CohortCharts";
import {
  formatCompact,
  num,
  productivityCoveragePct,
} from "./cohortInsights";
import { formatDate, formatNumber } from "../../../utils/formatters";

const { Text } = Typography;

function coverageTag(pct) {
  if (pct == null || Number.isNaN(pct))
    return <Tag color="default">—</Tag>;
  const p = Math.round(pct);
  const color = p >= 80 ? "success" : p >= 50 ? "warning" : "error";
  return (
    <Tag color={color} style={{ marginInlineEnd: 0 }}>
      {p}% active
    </Tag>
  );
}

const CohortViewsView = ({
  cohortViews = [],
  cohortViewsLoading = false,
  granularity = "weekly",
  onGranularityChange,
  level = "p",
}) => {
  const [displayMode, setDisplayMode] = useState("chart");

  const columns = useMemo(() => {
    const cols = [];

    if (level === "p") {
      cols.push({
        title: "Objective",
        dataIndex: "po_name",
        key: "po_name",
        width: 160,
        ellipsis: true,
        render: (val) => (
          <Tooltip title={val != null ? String(val) : ""}>
            <Text strong ellipsis style={{ maxWidth: 148, display: "block" }}>
              {val != null ? String(val) : "—"}
            </Text>
          </Tooltip>
        ),
      });
    }

    cols.push(
      {
        title: "Cohort → Period",
        key: "when",
        width: level === "p" ? 200 : 220,
        fixed: "left",
        render: (_, row) => {
          const cohort = row.cohort_date != null ? formatDate(row.cohort_date) : "—";
          const period =
            granularity === "weekly"
              ? row.week_ending_date != null
                ? `Week ${formatDate(row.week_ending_date)}`
                : "—"
              : row.calendar_date != null
                ? formatDate(row.calendar_date)
                : "—";
          return (
            <div>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Cohort
                </Text>
                <br />
                <Text>{cohort}</Text>
              </div>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {granularity === "weekly" ? "Week ending" : "Day"}
                </Text>
                <br />
                <Text>{period}</Text>
              </div>
            </div>
          );
        },
      },
      {
        title: "Team",
        key: "team",
        width: 130,
        align: "center",
        render: (_, row) => {
          const size = num(row.cohort_size);
          const cov = productivityCoveragePct(row);
          return (
            <div>
              <Text strong style={{ fontSize: 15 }}>
                {size ? formatNumber(size, 0, 0) : "—"}
              </Text>
              <div style={{ marginTop: 4 }}>{coverageTag(cov)}</div>
            </div>
          );
        },
      },
      {
        title: "Volume",
        key: "volume",
        width: 140,
        align: "right",
        render: (_, row) => {
          const h = num(row.cohort_total_hours);
          const u = num(row.cohort_total_units);
          return (
            <div>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Hours
                </Text>{" "}
                <Text strong>{formatCompact(h, 1)}</Text>
              </div>
              <div style={{ marginTop: 2 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Units
                </Text>{" "}
                <Text>{formatCompact(u, 0)}</Text>
              </div>
            </div>
          );
        },
      },
      {
        title: "Efficiency",
        key: "efficiency",
        width: 130,
        align: "right",
        render: (_, row) => {
          const rph = num(row.cohort_weighted_avg_rph);
          return (
            <div>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  RPH
                </Text>{" "}
                <Text strong style={{ color: "#0891b2" }}>
                  {formatNumber(rph, 1, 2)}
                </Text>
              </div>
              <div style={{ marginTop: 2 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  H / member
                </Text>{" "}
                <Text>{formatNumber(num(row.avg_hours_per_cohort_member), 1, 2)}</Text>
              </div>
            </div>
          );
        },
      },
      {
        title: "Per-member units",
        key: "units_member",
        width: 110,
        align: "right",
        render: (_, row) => (
          <Text>{formatNumber(num(row.avg_units_per_cohort_member), 1, 2)}</Text>
        ),
      },
    );

    if (granularity === "weekly") {
      cols.push({
        title: "Quality",
        key: "quality",
        width: 140,
        render: (_, row) => (
          <div>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Week
              </Text>{" "}
              <Text>{formatNumber(num(row.cohort_avg_weekly_quality), 2, 4)}</Text>
            </div>
            <div style={{ marginTop: 2 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                6 wk
              </Text>{" "}
              <Text>{formatNumber(num(row.cohort_avg_six_week_quality), 2, 4)}</Text>
            </div>
          </div>
        ),
      });
    }

    return cols;
  }, [level, granularity]);

  if (cohortViewsLoading) {
    return (
      <Card
        style={{
          marginTop: 0,
          borderRadius: 12,
          border: "1px solid #e8e8e8",
          boxShadow: "none",
        }}
        variant="borderless"
        styles={{
          body: { padding: "24px" },
        }}
      >
        <div style={{ textAlign: "center", padding: 32 }}>
          <Spin tip="Loading cohort views..." />
        </div>
      </Card>
    );
  }

  if (cohortViews.length === 0) {
    return (
      <Card
        style={{
          marginTop: 0,
          borderRadius: 12,
          border: "1px solid #e8e8e8",
        }}
        variant="borderless"
        styles={{
          body: { padding: "24px" },
        }}
      >
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: "#8c8c8c",
          }}
        >
          No cohort views available.
        </div>
      </Card>
    );
  }

  return (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Space wrap size="middle" split={<Divider type="vertical" style={{ margin: 0 }} />}>
          <Segmented
            options={[
              { label: "Weekly", value: "weekly" },
              { label: "Daily", value: "daily" },
            ]}
            value={granularity}
            onChange={onGranularityChange}
          />
          <Segmented
            options={[
              {
                label: (
                  <Space size={4}>
                    <LineChartOutlined />
                    Charts
                  </Space>
                ),
                value: "chart",
              },
              {
                label: (
                  <Space size={4}>
                    <TableOutlined />
                    Table
                  </Space>
                ),
                value: "table",
              },
            ]}
            value={displayMode}
            onChange={setDisplayMode}
          />
        </Space>
      </Col>
      {cohortViews.map((cohort) => {
        const rows = cohort.rows || [];
        return (
          <Col xs={24} key={cohort.id}>
            <Card
              type="inner"
              title={
                <Space>
                  <PartitionOutlined style={{ color: "#0891b2" }} />
                  {cohort.name}
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              {rows.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    textAlign: "center",
                    color: "#8c8c8c",
                  }}
                >
                  No data for this cohort view.
                </div>
              ) : displayMode === "chart" ? (
                <CohortCharts rows={rows} granularity={granularity} />
              ) : (
                <Table
                  size="small"
                  bordered
                  columns={columns}
                  dataSource={rows.map((r, i) => ({
                    ...r,
                    key: `${cohort.id}-${i}`,
                  }))}
                  pagination={{
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50"],
                    showTotal: (total) => `${total} cohort × period rows`,
                  }}
                  scroll={{ x: 900, y: 420 }}
                />
              )}
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

export default CohortViewsView;
